import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import PropertyCard from "../components/PropertyCard";
import "swiper/css";
import "swiper/css/pagination";
import "./blogdetail.css";

const API =
  process.env.REACT_APP_API_URL || "https://nvrealestate.websiteseoonline.com";

function BlogDetails() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchBlog = async () => {
      try {
        setLoading(true);
        const res = await axios.post(
          `${API}/api/blogsapinew.ashx`,
          { id },
          { headers: { "Content-Type": "application/json" } }
        );

        if (isMounted) {
          setProperty(res.data.data?.[0] || null);
        }
      } catch (error) {
        console.error("Error fetching blog:", error);
        if (isMounted) setProperty(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchBlog();
    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    let isMounted = true;

    const fetchRecentPosts = async () => {
      try {
        const res = await axios.post(
          `${API}/api/blogsall.ashx`,
          { page: 1, pageSize: 6, sortBy: "id DESC" },
          { headers: { "Content-Type": "application/json" } }
        );

        if (isMounted && res.data?.result === 1) {
          setRecentPosts(
            (res.data.data || [])
              .filter((post) => String(post.id) !== String(id))
              .slice(0, 5)
          );
        }
      } catch (error) {
        console.error("Error fetching recent blogs:", error);
      }
    };

    fetchRecentPosts();
    return () => {
      isMounted = false;
    };
  }, [id]);

  const readTime = useMemo(() => {
    const text = stripHtml(property?.Description || "");
    return Math.max(1, Math.ceil(text.split(/\s+/).filter(Boolean).length / 220));
  }, [property]);

  const filteredPosts = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return recentPosts;
    return recentPosts.filter((post) =>
      (post.title || "").toLowerCase().includes(query)
    );
  }, [recentPosts, searchTerm]);

  if (loading) {
    return (
      <main className="blog-detail-state" aria-live="polite">
        <span className="blog-detail-spinner" />
        <p>Loading article...</p>
      </main>
    );
  }

  if (!property) {
    return (
      <main className="blog-detail-state">
        <h1>Blog not found</h1>
        <p>The article may have been moved or is no longer available.</p>
        <Link to="/blogs" className="blog-detail-back">
          View all articles
        </Link>
      </main>
    );
  }

  const image = appendImageTransform(property.Image, "w=1600,sharpen=1");
  const date = formatDate(property.date || property.createddate || property.Date);
  const agentName = property.agentname || property.agentName || "";
  const agentImage = property.agentimage || property.agentImage || "";
  const agentUrl = property.agenturl || property.agentUrl || "";
  const author =
    agentName ||
    property.author ||
    property.Author ||
    property.postedby ||
    "NV Real Estate";
  const selectedProperties = Array.isArray(property.properties)
    ? property.properties
    : [];
  const selectedProjects = Array.isArray(property.newprojects)
    ? property.newprojects
    : [];

  return (
    <main className="blog-detail-page">
      <section
        className="blog-detail-hero"
        style={{ backgroundImage: `url(${image})` }}
      >
        <div className="blog-detail-hero-shade" />
        <div className="blog-detail-shell blog-detail-hero-content">
          <Link to="/blogs" className="blog-detail-label">Blog</Link>
          <h1>{property.title}</h1>
          <div className="blog-detail-meta">
            {agentUrl ? (
              <Link className="blog-detail-author" to={agentUrl}>
                {agentImage ? (
                  <img
                    className="blog-detail-author-image"
                    src={appendImageTransform(
                      agentImage,
                      "w=100,h=100,fit=cover,sharpen=1"
                    )}
                    alt={author}
                  />
                ) : (
                  <span className="blog-detail-author-icon" aria-hidden="true">
                    {getInitials(author)}
                  </span>
                )}
                <span>{author}</span>
              </Link>
            ) : (
              <>
                <span className="blog-detail-author-icon" aria-hidden="true">
                  <i className="far fa-user" />
                </span>
                <span>{author}</span>
              </>
            )}
            {date && <span className="blog-detail-meta-divider">{date}</span>}
            <span className="blog-detail-meta-divider">{readTime} min read</span>
          </div>
        </div>
      </section>

      <section className="blog-detail-content-section">
        <div className="blog-detail-shell blog-detail-layout">
          <article className="blog-detail-article">
            <div
              className="blog-detail-copy"
              dangerouslySetInnerHTML={{ __html: property.Description }}
            />

          </article>

          <aside className="blog-detail-sidebar" aria-label="Blog sidebar">
            <div className="blog-detail-sidebar-card">
              <label className="blog-detail-search">
                <span className="sr-only">Search recent posts</span>
                <input
                  type="search"
                  placeholder="Search blog..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                <span className="blog-detail-search-button" aria-hidden="true">
                  <i className="fas fa-search" />
                </span>
              </label>

              <SidebarHeading>Recent Posts</SidebarHeading>
              <div className="blog-detail-recent-list">
                {filteredPosts.map((post) => (
                  <Link
                    className="blog-detail-recent-post"
                    to={post.url || `/blog/${slugify(post.title)}/${post.id}`}
                    key={post.id}
                  >
                    <img
                      src={appendImageTransform(post.Image, "w=240,sharpen=1")}
                      alt=""
                      loading="lazy"
                    />
                    <span>
                      <strong>{post.title}</strong>
                      <small>
                        {formatDate(post.date || post.createddate || post.Date)}
                      </small>
                    </span>
                  </Link>
                ))}
                {filteredPosts.length === 0 && (
                  <p className="blog-detail-no-posts">No matching posts found.</p>
                )}
              </div>

             

              <div className="blog-detail-property-cta">
                <span className="blog-detail-building-lines" aria-hidden="true" />
                <h3>Find Your Dream Property in the UAE</h3>
                <p>Browse our handpicked listings or contact our experts today.</p>
                <Link to="/properties-for-sale">Browse Properties</Link>
              </div>
            </div>
          </aside>
        </div>

        <div className="blog-detail-shell blog-detail-related-content">
          {selectedProperties.length > 0 && (
            <RelatedPropertySection title="Properties for Buy & Rent">
              {selectedProperties.map((item) => (
                <PropertyCard key={item.id} item={item} />
              ))}
            </RelatedPropertySection>
          )}

          {selectedProjects.length > 0 && (
            <RelatedPropertySection title="New Projects">
              {selectedProjects.map((item) => (
                <NewProjectCard key={item.id} item={item} />
              ))}
            </RelatedPropertySection>
          )}

          <div className="blog-detail-article-footer">
            <Link to="/blogs">
              <i className="fas fa-arrow-left" aria-hidden="true" />
              Back to all articles
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function SidebarHeading({ children }) {
  return (
    <h2 className="blog-detail-sidebar-title">
      {children}
      <span aria-hidden="true" />
    </h2>
  );
}

function RelatedPropertySection({ title, children }) {
  const cards = React.Children.toArray(children);
  const useSlider = cards.length > 3;

  return (
    <section className="blog-detail-related" aria-label={title}>
      <h2>{title}</h2>
      {useSlider ? (
        <Swiper
          modules={[Pagination, Autoplay]}
          spaceBetween={30}
          slidesPerView={3}
          pagination={{ clickable: true }}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          breakpoints={{
            320: { slidesPerView: 1, spaceBetween: 15 },
            768: { slidesPerView: 2, spaceBetween: 24 },
            1024: { slidesPerView: 3, spaceBetween: 30 },
          }}
          className="blog-detail-property-slider"
        >
          {cards.map((card) => (
            <SwiperSlide key={card.key}>{card}</SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div className="row blog-detail-property-grid">{cards}</div>
      )}
    </section>
  );
}

function NewProjectCard({ item }) {
  const propertyTypes = Array.isArray(item.property_type)
    ? item.property_type
    : String(item.property_type || "").split("|");
  const detailUrl = `/off-plan-property/${item.url}`;

  return (
    <div className="col-xl-4 col-lg-6 col-md-6">
      <div className="homelengo-box">
        <div className="archive-top">
          <Link className="images-group" to={detailUrl}>
            <div className="images-style">
              <img
                src={appendImageTransform(item.ImagePath, "w=421,sharpen=1")}
                alt={item.title}
                loading="lazy"
              />
            </div>
            <div className="top">
              <ul className="d-flex gap-6">
                {item.paymentplan && (
                  <li className="flag-tag primary">
                    {item.paymentplan} Payment Plan
                  </li>
                )}
                {item.moveinstatus && (
                  <li className="flag-tag style-1">{item.moveinstatus}</li>
                )}
              </ul>
            </div>
          </Link>
        </div>
        <div className="archive-bottom">
          <div className="content-top">
            <h6 className="text-capitalize">
              <Link className="link" to={detailUrl}>{item.title}</Link>
            </h6>
            <p>{[item.community, item.city].filter(Boolean).join(", ")}</p>
            <ul className="meta-list">
              {propertyTypes.filter((type) => type.trim()).map((type) => (
                <li key={type.trim()} className="item border">{type.trim()}</li>
              ))}
            </ul>
          </div>
          <div className="content-bottom mb-10">
            <Link className="whitefont fullwidth" to={detailUrl}>
              <h6 className="price">Starting From AED {formatPrice(item.price)}</h6>
            </Link>
          </div>
          <CardContactLinks title={item.title} />
        </div>
      </div>
    </div>
  );
}

function CardContactLinks({ title }) {
  const message = encodeURIComponent(
    `Hi, I am interested in ${title} and would like more information. Thank you.`
  );
  return (
    <div className="property-icns-w">
      <a href="tel:+971501577822">
        <i className="icon"><img src="/images/img/phone.svg" alt="" /></i>Call
      </a>
      <a href="mailto:info@nvrealestate.ae">
        <i className="icon"><img src="/images/img/email.svg" alt="" /></i>Email
      </a>
      <a href={`https://wa.me/971544544287?text=${message}`} target="_blank" rel="noreferrer">
        <i className="icon"><img src="/images/img/whatsapp.svg" alt="" /></i>Whatsapp
      </a>
    </div>
  );
}

function appendImageTransform(url, transform) {
  if (!url) return `${process.env.PUBLIC_URL}/assets/images/inner-banner.jpg`;
  return `${url}${url.endsWith("/") ? "" : "/"}${transform}`;
}

function stripHtml(html) {
  return html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ");
}

function getInitials(name) {
  return (
    String(name || "NV")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join("") || "NV"
  );
}

function formatPrice(value) {
  const numericValue = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(numericValue) && numericValue > 0
    ? numericValue.toLocaleString()
    : value || "On Request";
}

function slugify(value = "blog") {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default BlogDetails;
